import { useDataChainId } from '@ui/hooks/datachain';
import { useQuery } from '@tanstack/react-query';
import { parseAbiItem } from 'viem';
import { usePublicClient } from 'wagmi';

const MONAD_STAKING_ADDRESS = '0x0000000000000000000000000000000000001000';
const EPOCH_KEEPER_ADDRESS =
  '0xBDF8292bc05c0380C273A04Ec7cd799FBa3cBD34' as const;
const EPOCH_CHANGED_EVENT = parseAbiItem(
  'event EpochChanged(uint64 oldEpoch, uint64 newEpoch)',
);
const EPOCH_LOG_CHUNK_SIZE = 100n;
const EPOCH_LOG_BATCH_SIZE = 12;
const EPOCH_SEARCH_BUFFER_BLOCKS = 1000n;
const KEEPER_LOOKBACK_BUFFER_BLOCKS = 500n;
const KEEPER_BINARY_SEARCH_RANGE_BLOCKS = 51_000n;
const EPOCH_LOG_LOOKBACK_BUFFER_BLOCKS = 100n;
export const EPOCH_BLOCKS = 55_000;
const EPOCH_BLOCKS_BIGINT = 55_000n;
const BLOCK_TIME_MS = 400;
const TIMESTAMP_MS_MULTIPLIER = 1000n;
export const EPOCH_DURATION_MS = EPOCH_BLOCKS * BLOCK_TIME_MS;

// Cache the offset in memory to avoid repeated expensive searches
let cachedOffset: bigint | null = null;
let cachedKeeperNonce: number | null = null;
let cachedKeeperLastTxBlock: bigint | null = null;
let cachedEpochResult: {
  epoch: bigint;
  startTimeMs: number;
  blockNumber: bigint;
} | null = null;

const toTimestampMs = (timestamp: bigint): number => {
  return Number(timestamp * TIMESTAMP_MS_MULTIPLIER);
};

const subtractOrZero = (value: bigint, delta: bigint): bigint => {
  if (value <= delta) {
    return 0n;
  }

  return value - delta;
};

const maxBigInt = (left: bigint, right: bigint): bigint => {
  if (left > right) {
    return left;
  }

  return right;
};

export const useMonadEpoch = () => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });

  return useQuery({
    queryKey: ['monad-epoch-start', chainId.toString()],
    queryFn: async () => {
      if (!publicClient) return null;

      const currentBlockNumber = await publicClient.getBlockNumber();
      const epochBlocksBigInt = EPOCH_BLOCKS_BIGINT;
      const maxSearchBlocks = epochBlocksBigInt + EPOCH_SEARCH_BUFFER_BLOCKS;
      const latestKeeperNonce = await publicClient.getTransactionCount({
        address: EPOCH_KEEPER_ADDRESS,
      });

      if (
        cachedEpochResult &&
        cachedKeeperNonce !== null &&
        latestKeeperNonce === cachedKeeperNonce
      ) {
        return {
          epoch: cachedEpochResult.epoch,
          startTimeMs: cachedEpochResult.startTimeMs,
        };
      }

      const findKeeperLastTxBlock = async (): Promise<bigint | null> => {
        if (latestKeeperNonce === 0) return null;

        const runBinarySearch = async (leftBoundary: bigint) => {
          let left = leftBoundary;
          let right = currentBlockNumber;
          let answer: bigint | null = null;

          while (left <= right) {
            const mid = (left + right) / 2n;
            const nonceAtMid = await publicClient.getTransactionCount({
              address: EPOCH_KEEPER_ADDRESS,
              blockNumber: mid,
            });

            if (nonceAtMid >= latestKeeperNonce) {
              answer = mid;
              if (mid === 0n) break;
              right = mid - 1n;
            } else {
              left = mid + 1n;
            }
          }

          return answer;
        };

        let boundedLeftByRange = 0n;
        if (currentBlockNumber > KEEPER_BINARY_SEARCH_RANGE_BLOCKS) {
          boundedLeftByRange =
            currentBlockNumber - KEEPER_BINARY_SEARCH_RANGE_BLOCKS;
        }
        const boundedLeftByCache = cachedKeeperLastTxBlock ?? 0n;
        const boundedLeft = maxBigInt(boundedLeftByCache, boundedLeftByRange);

        const boundedAnswer = await runBinarySearch(boundedLeft);
        if (boundedAnswer === null) return runBinarySearch(0n);
        if (boundedAnswer > boundedLeft || boundedAnswer === 0n) {
          return boundedAnswer;
        }

        const nonceBeforeBoundedLeft = await publicClient.getTransactionCount({
          address: EPOCH_KEEPER_ADDRESS,
          blockNumber: boundedLeft - 1n,
        });
        if (nonceBeforeBoundedLeft >= latestKeeperNonce) {
          return runBinarySearch(0n);
        }

        return boundedAnswer;
      };

      // Helper to fetch logs in a specific range
      const fetchLogs = async (from: bigint, to: bigint) => {
        return publicClient.getLogs({
          address: MONAD_STAKING_ADDRESS,
          event: EPOCH_CHANGED_EVENT,
          fromBlock: from,
          toBlock: to,
        });
      };

      const findLatestEpochLogInRange = async (
        fromBlock: bigint,
        toBlock: bigint,
      ) => {
        if (fromBlock > toBlock) return null;

        const searchSpan = toBlock - fromBlock;
        const batchSpan = EPOCH_LOG_CHUNK_SIZE * BigInt(EPOCH_LOG_BATCH_SIZE);

        for (
          let baseOffset = 0n;
          baseOffset <= searchSpan;
          baseOffset += batchSpan
        ) {
          const promises = [];
          for (let j = 0; j < EPOCH_LOG_BATCH_SIZE; j++) {
            const offset = baseOffset + EPOCH_LOG_CHUNK_SIZE * BigInt(j);
            if (offset > searchSpan) break;

            let end = fromBlock;
            if (toBlock > offset) {
              end = toBlock - offset;
            }
            const chunkStart = subtractOrZero(end, EPOCH_LOG_CHUNK_SIZE);
            const start = maxBigInt(chunkStart, fromBlock);
            promises.push(fetchLogs(start, end));
          }

          const results = await Promise.all(promises);
          for (const logs of results) {
            if (logs.length === 0) continue;
            const latestLog = logs[logs.length - 1];
            if (latestLog.blockNumber === null || !latestLog.args) continue;
            const newEpoch = latestLog.args.newEpoch;
            if (newEpoch === undefined) continue;

            return {
              blockNumber: latestLog.blockNumber,
              newEpoch,
            };
          }

          if (toBlock <= baseOffset + batchSpan) break;
        }

        return null;
      };

      // Strategy 1: If we have a cached offset, use it to pinpoint the event
      if (cachedOffset !== null) {
        const blockDistance =
          (currentBlockNumber - cachedOffset) % epochBlocksBigInt;
        const estimatedStartBlock = currentBlockNumber - blockDistance;
        // Search small window around estimated point
        const fromBlock = subtractOrZero(estimatedStartBlock, 10n);
        const logs = await fetchLogs(fromBlock, estimatedStartBlock + 10n);
        if (logs.length > 0) {
          const latestLog = logs[logs.length - 1];
          if (latestLog.blockNumber === null || !latestLog.args) {
            cachedOffset = null;
          } else {
            const newEpoch = latestLog.args.newEpoch;
            if (newEpoch === undefined) {
              cachedOffset = null;
            } else {
              const block = await publicClient.getBlock({
                blockNumber: latestLog.blockNumber,
              });
              cachedKeeperNonce = latestKeeperNonce;
              const result = {
                epoch: newEpoch,
                startTimeMs: toTimestampMs(block.timestamp),
              };
              cachedEpochResult = {
                ...result,
                blockNumber: latestLog.blockNumber,
              };
              return result;
            }
          }
        }
        // If cached offset failed (e.g. reorg or genesis reset), fall through to full search
        cachedOffset = null;
      }

      // Strategy 2: Search backwards from current block
      // We search up to 1 epoch + buffer (56000 blocks)
      // We batch requests to respect RPC limits (1000 blocks) and speed up (parallel)
      const keeperLastTxBlock = await findKeeperLastTxBlock();
      const fallbackSearchFrom = subtractOrZero(
        currentBlockNumber,
        maxSearchBlocks,
      );
      let keeperSearchFrom = fallbackSearchFrom;
      if (keeperLastTxBlock !== null) {
        keeperSearchFrom = subtractOrZero(
          keeperLastTxBlock,
          KEEPER_LOOKBACK_BUFFER_BLOCKS,
        );
      }
      const searchFromBlock = maxBigInt(keeperSearchFrom, fallbackSearchFrom);
      let knownEpochLogSearchFrom = 0n;
      if (cachedEpochResult?.blockNumber !== undefined) {
        knownEpochLogSearchFrom = subtractOrZero(
          cachedEpochResult.blockNumber,
          EPOCH_LOG_LOOKBACK_BUFFER_BLOCKS,
        );
      }
      const effectiveSearchFromBlock = maxBigInt(
        knownEpochLogSearchFrom,
        searchFromBlock,
      );

      // Fast lane: directly probe around keeper's latest tx block.
      if (keeperLastTxBlock !== null) {
        const keeperExactLog = await findLatestEpochLogInRange(
          keeperLastTxBlock,
          keeperLastTxBlock,
        );
        if (keeperExactLog) {
          cachedOffset = keeperExactLog.blockNumber % epochBlocksBigInt;
          cachedKeeperNonce = latestKeeperNonce;
          cachedKeeperLastTxBlock = keeperLastTxBlock;
          const block = await publicClient.getBlock({
            blockNumber: keeperExactLog.blockNumber,
          });
          const result = {
            epoch: keeperExactLog.newEpoch,
            startTimeMs: toTimestampMs(block.timestamp),
          };
          cachedEpochResult = {
            ...result,
            blockNumber: keeperExactLog.blockNumber,
          };
          return result;
        }

        const keeperProbeFrom = subtractOrZero(
          keeperLastTxBlock,
          KEEPER_LOOKBACK_BUFFER_BLOCKS,
        );
        const keeperProbeTo =
          keeperLastTxBlock + KEEPER_LOOKBACK_BUFFER_BLOCKS < currentBlockNumber
            ? keeperLastTxBlock + KEEPER_LOOKBACK_BUFFER_BLOCKS
            : currentBlockNumber;
        const keeperProbeLog = await findLatestEpochLogInRange(
          keeperProbeFrom,
          keeperProbeTo,
        );
        if (keeperProbeLog) {
          cachedOffset = keeperProbeLog.blockNumber % epochBlocksBigInt;
          cachedKeeperNonce = latestKeeperNonce;
          cachedKeeperLastTxBlock = keeperLastTxBlock;
          const block = await publicClient.getBlock({
            blockNumber: keeperProbeLog.blockNumber,
          });
          const result = {
            epoch: keeperProbeLog.newEpoch,
            startTimeMs: toTimestampMs(block.timestamp),
          };
          cachedEpochResult = {
            ...result,
            blockNumber: keeperProbeLog.blockNumber,
          };
          return result;
        }
      }

      const fallbackLog = await findLatestEpochLogInRange(
        effectiveSearchFromBlock,
        currentBlockNumber,
      );
      if (fallbackLog) {
        cachedOffset = fallbackLog.blockNumber % epochBlocksBigInt;
        cachedKeeperNonce = latestKeeperNonce;
        cachedKeeperLastTxBlock = keeperLastTxBlock;
        const block = await publicClient.getBlock({
          blockNumber: fallbackLog.blockNumber,
        });
        const result = {
          epoch: fallbackLog.newEpoch,
          startTimeMs: toTimestampMs(block.timestamp),
        };
        cachedEpochResult = {
          ...result,
          blockNumber: fallbackLog.blockNumber,
        };
        return result;
      }

      // Fallback: If still not found (should not happen if chain > 55000 blocks),
      // assume offset 0 and estimate
      console.warn(
        'EpochChanged event not found in recent history, using fallback estimation',
      );
      const estimatedStartBlock =
        currentBlockNumber - (currentBlockNumber % epochBlocksBigInt);
      const block = await publicClient.getBlock({
        blockNumber: estimatedStartBlock,
      });
      const estimatedEpoch = estimatedStartBlock / epochBlocksBigInt + 1n;
      cachedKeeperNonce = latestKeeperNonce;
      cachedKeeperLastTxBlock = keeperLastTxBlock;
      cachedEpochResult = {
        epoch: estimatedEpoch,
        startTimeMs: toTimestampMs(block.timestamp),
        blockNumber: estimatedStartBlock,
      };

      return {
        epoch: estimatedEpoch,
        startTimeMs: toTimestampMs(block.timestamp),
      };
    },
    enabled: !!publicClient,
    refetchInterval: 300_000, // Refetch less frequently (5 mins) since we have caching logic
    staleTime: 300_000,
  });
};
